/**
 * Lesson runtime — the platform-portable contract for serving lessons.
 *
 * Two access patterns:
 *
 *   1. Synchronous *metadata* lookup — `listLessons()`,
 *      `getLessonMetadata(nodeId)`, `hasLessonForNode(nodeId)`. Reads
 *      the hand-rolled `metadata-registry.ts` and never touches MDX
 *      content. Cheap, can be called per-render to drive index pages
 *      and skill-tree affordances.
 *
 *   2. Asynchronous *module* load — `loadLesson(nodeId)`. Returns the
 *      compiled MDX component. Each loader is a literal `import("…")`
 *      so Vite splits each MDX module + its transitive lesson-helper
 *      imports into a per-lesson chunk (spec §14: < 30 KB gz/lesson).
 *
 * Why this split: the lesson barrel re-exports `<Score>`, `<PlayButton>`,
 * `<MiniExercise>`, and the audio context — collectively a few hundred
 * KB of static surface. Importing the runtime contract through this
 * file (or, equivalently, `@music-app/lessons/runtime`) keeps the index
 * page's static graph free of those components.
 */

import type { ComponentType } from "react";

import { lessonRegistry, asLessonModule } from "./metadata-registry.js";

export interface LessonMetadata {
  /** Curriculum node this lesson covers. Stable string ID. */
  nodeId: string;
  /** Display title shown in the lesson list and at the top of the lesson. */
  title: string;
  /** Author-estimated read time in minutes (target 5–10). */
  estReadMinutes: number;
  /**
   * Items the lesson asks the learner to check via `<MiniExercise>`. The
   * lesson UI gates "completed" state once every itemId has been
   * answered correctly at least once.
   */
  checkFor: { itemId: string }[];
}

export interface LessonModule extends LessonMetadata {
  /** The compiled MDX component. Render with `<lesson.default />`. */
  default: ComponentType;
}

/**
 * List every authored lesson's metadata, in registry order. Drives the
 * `/learn` index. Returns metadata only — the MDX components stay out
 * of the page so loading the list doesn't pull in every lesson's prose.
 */
export function listLessons(): LessonMetadata[] {
  return lessonRegistry().map((e) => e.metadata);
}

/**
 * Synchronous metadata lookup for a single node. Returns `null` when
 * no lesson is authored — callers (skill-tree tiles, /learn cards)
 * treat that as "no Lesson link to show."
 */
export function getLessonMetadata(nodeId: string): LessonMetadata | null {
  return lessonRegistry().find((e) => e.metadata.nodeId === nodeId)?.metadata ?? null;
}

/** Quick existence check that doesn't allocate the metadata object. */
export function hasLessonForNode(nodeId: string): boolean {
  return lessonRegistry().some((e) => e.metadata.nodeId === nodeId);
}

/**
 * Load the compiled MDX module for a node. Each call kicks off the
 * dynamic import for that lesson's per-lesson chunk; subsequent calls
 * for the same nodeId hit the bundler's module cache. Returns `null`
 * when no lesson is authored for the node so the route can render a
 * not-found state without throwing.
 */
export async function loadLesson(nodeId: string): Promise<LessonModule | null> {
  const entry = lessonRegistry().find((e) => e.metadata.nodeId === nodeId);
  if (!entry) return null;
  const mod = await entry.loader();
  return asLessonModule(mod, entry.metadata);
}
